import { useEffect, useMemo, useState } from "react";
import momentTZ from "moment-timezone";
import { Layout, ConfigProvider, Result } from "antd";

import {
  ActionTypes,
  useMercuryContext,
  useMercuryDispatch,
} from "../context";

import { useLocation, useNavigate, Link } from "react-router-dom";

import "../styles/variables.css";
import "./App.scss";

import Header from "../components/navbar/Header";
import SideAdminMenu from "../components/navbar/SideAdminMenu";
import ContentSection from "../components/navbar/Content";
import LoginPage from "auth/login/LoginPage";
import SessionExpiredModal from "auth/login/SessionExpired";
import { usePermissions } from "../hooks/usePermissions";
import { ROUTES } from "../common/constants/routes";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import UnauthorizedPage from "auth/login/Unauthorized";

const normalizeAdminMenu = (menus: any[]) => {
  return menus
    .map(menu => {
      if (!menu.active) return null;

      const filteredChildren = menu.children
        ?.map((child: any) => {
          if (child.submenu) {
            const activeSubmenu = child.submenu.filter((s: any) => s.active);
            return activeSubmenu.length
              ? { ...child, submenu: activeSubmenu }
              : null;
          }

          return child.active ? child : null;
        })
        .filter(Boolean);

      return filteredChildren?.length
        ? { ...menu, children: filteredChildren }
        : null;
    })
    .filter(Boolean);
};

function App() {
  const mercuryDispatch = useMercuryDispatch(),
    navigate = useNavigate(),
    location = useLocation(),
    { isAdmin, loginRequired, flags, selectedApp, userDetails } = useMercuryContext(),
    [visibleAdminMenuItems, setVisibleAdminMenuItems] = useState([]),
    currentLocation = location ? location.pathname : "";

  const [currentMenuItem, setCurrentMenuItem] = useState("");
  const { hasPermissions } = usePermissions();
  const permissionsArray = Array.isArray(userDetails?.permissions) ? userDetails.permissions : [];
  const hasAnyPermissions = permissionsArray.length > 0;

  const handleMainMenuSelection = (e: any) => {
    setCurrentMenuItem(e);
  };

  const selectedAppName = useMemo(() => {
    if (!selectedApp || !userDetails?.customerApplicationDetails || userDetails?.customerApplicationDetails?.length === 0) return null;
    return userDetails?.customerApplicationDetails.find((a:any) => a._id === selectedApp)?.name || null;
  }, [selectedApp, userDetails?.customerApplicationDetails]);

  const hasMercuryAccess = useMemo(
    () =>
      (userDetails?.customerApplicationDetails || []).some(
        (a: any) => a.name === "Mercury"
      ),
    [userDetails?.customerApplicationDetails]
  );

  const canViewExtensionJustifications = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Extension Justifications",
      action: "read",
    },
  ]);
  const canViewManageNotifications = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Delayed Status Notification",
      action: "read",
    },
  ]);

  const canViewProducts = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Manage Products",
      action: "read",
    },
  ]);

  const canViewProductAreas = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Manage Product Areas",
      action: "read",
    },
  ]);

  const canViewHelixTemplateMappings = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Helix Template Mapping",
      action: "read",
    },
  ]);

  const canViewHelixTemplates = hasPermissions([
    {
      customerApplication: "Mercury",
      permissionName: "Helix Templates",
      action: "read",
    },
  ]);

  const canViewRates = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Project Rates",
      action: "read",
    },
  ]);

  const canViewCostCode = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Cost Code",
      action: "read",
    },
  ]);

  const canViewCostCodeRules = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Cost Code Rules",
      action: "read",
    },
  ]);

  const canViewCurrencies = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Currencies",
      action: "read",
    },
  ]);

  const canViewManageReviewHourMappings = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Review Hour Mapping",
      action: "read",
    },
  ]);

  const canViewManageTranslationHourMappings = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Translation Hour Mapping",
      action: "read",
    },
  ]);

  const canViewManageVARServiceTypes = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "VAR Service Types",
      action: "read",
    },
  ]);

  const canViewAppSettings = hasPermissions([
    {
      customerApplication: "Mercury",
      permissionName: "Settings",
      action: "read",
    },
  ]);

  const canViewManageTeams = hasPermissions([
    {
      customerApplication: "Mercury",
      permissionName: "Manage Teams",
      action: "read",
    },
  ]);

  const canViewManageUsers = hasPermissions([
    {
      customerApplication: "Mercury",
      permissionName: "Users",
      action: "read",
    },
  ]);

  const canViewManageRoles = hasPermissions([
    {
      customerApplication: "Mercury",
      permissionName: "Manage Roles",
      action: "read",
    },
  ]);

  const canViewApiClients = hasPermissions([
    {
      customerApplication: "Mercury",
      permissionName: "Api Clients",
      action: "read",
    },
  ]);

  const canViewInvoiceTypes = 
      hasPermissions([
    {
      customerApplication: "Mercury",
      permissionName: "Invoice Types",
      action: "read",
    },
  ]);

  const canViewInvoiceLineItems =
      hasPermissions([
    {
      customerApplication: "Mercury",
      permissionName: "Invoice Line Items",
      action: "read",
    },
  ]);
  const canViewWorklogDefinitions =
      hasPermissions([
    {
      customerApplication: "Mercury",
      permissionName: "Worklog Definitions",
      action: "read",
    },
  ]);
  const canViewManageServices = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Services",
      action: "read",
    },
  ]);

  const canViewLanguages = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Languages",
      action: "read",
    },
  ]);

  const canViewWorkflowStages = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Workflow Stages",
      action: "read",
    },
  ]);

  const canViewProjectTypeRules = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Project Type Rules",
      action: "read",
    },
  ]);

  const canViewSharingConfiguration = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Sharing Configuration",
      action: "read",
    },
  ]);

  const canViewEmpowerDepartments = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Empower Departments",
      action: "read",
    },
  ]);

  const canViewWorkflowTypes = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Workflow Types",
      action: "read",
    },
  ]);

  const canViewManageDownload = hasPermissions([
    {
      customerApplication: "Google",
      permissionName: "Manage Download",
      action: "read",
    },
  ]);

  // Admin Menu
  // to add a submenu, remove the path key and add submenu key as array.
  const adminMenuItems = [
    {
      id: 0,
      title: "System Administration",
      active: hasMercuryAccess,
      children: [
        {
          id: 0,
          path: ROUTES.MANAGE_USERS,
          title: "Manage Users",
          active: canViewManageUsers,
          parentId: 0,
        },
        {
          id: 1,
          path: ROUTES.MANAGE_TEAMS,
          title: "Manage Teams",
          active: canViewManageTeams,
          parentId: 0,
        },
        {
          id: 2,
          path: ROUTES.MANAGE_ROLES,
          title: "Manage Roles",
          active: canViewManageRoles,
          parentId: 0,
        },
        {
          id: 3,
          path: ROUTES.MANAGE_API_CLIENTS,
          title: "Manage Api Clients",
          active: canViewApiClients,
          parentId: 0,
        },
        {
          id: 4,
          path: ROUTES.MANAGE_COST_CODES,
          title: "Manage Cost Codes",
          active: canViewCostCode,
          parentId: 0,
        },
        {
          id: 5,
          path: ROUTES.MANAGE_CURRENCIES,
          title: "Manage Currencies",
          active: canViewCurrencies,
          parentId: 0,
        },
        {
          id: 6,
          path: ROUTES.MANAGE_HELIX_TEMPLATES,
          title: "Manage Helix Templates",
          active: canViewHelixTemplates,
          parentId: 0,
        },
        {
          id: 7,
          path: ROUTES.MANAGE_PO_TYPES,
          title: "Manage PO Types",
          active: isAdmin,
          parentId: 0,
        },
        {
          id: 8,
          path: ROUTES.MANAGE_INVOICE_TYPES,
          title: "Manage Invoice Types",
          active: canViewInvoiceTypes,
          parentId: 0,
        },
        {
          id: 10,
          path: ROUTES.MANAGE_INVOICE_LINE_ITEMS,
          title: "Manage Invoice Line Items",
          active: canViewInvoiceLineItems,
          parentId: 0,
        },
        {
          id: 11,
          path: ROUTES.MANAGE_WORKLOG_DEFINITIONS,
          title: "Manage Worklog Definitions",
          active: canViewWorklogDefinitions,
          parentId: 0,
        },
        {
          id: 12,
          path: ROUTES.MANAGE_APP_SETTINGS,
          title: "Manage App Settings",
          active: canViewAppSettings,
          parentId: 0,
        },
        {
          id: 13,
          path: ROUTES.MANAGE_EMPOWER_DEPARTMENT,
          title: "Manage Empower Departments",
          active: canViewEmpowerDepartments,
          parentId: 0,
        },
      ],
    },
    {
      id: 1,
      title: "Google Administration",
      active: selectedAppName === 'Google',
      children: [
        {
          id: 10,
          title: "Product Management",
          parentId: 1,
          active: canViewProducts || canViewProductAreas,
          submenu: [
            {
              id: 100,
              path: ROUTES.GOOGLE_ADMIN_PRODUCTS,
              title: "Manage Products",
              active: canViewProducts,
              parentId: 10,
            },
            {
              id: 101,
              path: ROUTES.GOOGLE_ADMIN_PRODUCT_AREAS,
              title: "Manage Product Areas",
              active: canViewProductAreas,
              parentId: 10,
            },
          ],
        },
        {
          id: 11,
          path: ROUTES.GOOGLE_ADMIN_HELIX_TEMPLATE_MAPPINGS,
          title: "Manage Google Helix Template Mappings",
          active: canViewHelixTemplateMappings,
          parentId: 1,
        },
        {
          id: 12,
          path: ROUTES.GOOGLE_ADMIN_EXTENSION_JUSTIFICATIONS,
          title: "Manage Extension Justifications",
          active: canViewExtensionJustifications,
          parentId: 1,
        },
        {
          id: 13,
          path: ROUTES.GOOGLE_ADMIN_WORKFLOW_TYPES,
          title: "Manage Workflow Types",
          active: canViewWorkflowTypes,
          parentId: 1,
        },
        {
          id: 14,
          path: ROUTES.GOOGLE_ADMIN_QUARANTINED_PROJECTS,
          title: "Quarantined projects",
          active: canViewManageDownload && flags.manageDownload,
          parentId: 1,
        },
        {
          id: 15,
          path: ROUTES.GOOGLE_ADMIN_DOWNLOAD_PROJECT,
          title: "Download project",
          active: canViewManageDownload && flags.manageDownload,
          parentId: 1,
        },
        {
          id: 16,
          path: ROUTES.GOOGLE_ADMIN_FAILED_DOWNLOAD,
          title: "Failed to download",
          active: canViewManageDownload && flags.manageDownload,
          parentId: 1,
        },
        {
          id: 17,
          path: ROUTES.GOOGLE_ADMIN_DELAYED_STATUS_NOTIFICATION,
          title: "Manage Delayed Status Notification",
          active: canViewManageNotifications,
          parentId: 1,
        },
        {
          id: 18,
          path: ROUTES.GOOGLE_ADMIN_PROJECT_RATES,
          title: "Manage Project Rates",
          active: canViewRates,
          parentId: 1,
        },
        {
          id: 19,
          path: ROUTES.GOOGLE_ADMIN_REVIEW_HOUR_MAPPINGS,
          title: "Manage Review Hour Mappings",
          active: canViewManageReviewHourMappings,
          parentId: 1,
        },
        {
          id: 20,
          path: ROUTES.GOOGLE_ADMIN_SERVICES,
          title: "Manage Services",
          active: canViewManageServices,
          parentId: 1,
        },
        {
          id: 21,
          path: ROUTES.GOOGLE_ADMIN_MANAGE_TRANSLATION_HOURS,
          title: "Manage Translation Hour Mappings",
          active: canViewManageTranslationHourMappings,
          parentId: 1,
        },
        {
          id: 22,
          path: ROUTES.GOOGLE_ADMIN_VAR_SERVICE_TYPES,
          title: "Manage VAR Service Types",
          active: canViewManageVARServiceTypes,
          parentId: 1,
        },
        {
          id: 23,
          path: ROUTES.GOOGLE_ADMIN_LANGUAGES,
          title: "Manage Languages",
          active: canViewLanguages,
          parentId: 0,
        },
        {
          id: 24,
          path: ROUTES.GOOGLE_ADMIN_WORKFLOW_STAGES,
          title: "Manage Workflow Stages",
          active: canViewWorkflowStages,
          parentId: 1,
        },
        {
          id: 25,
          path: ROUTES.GOOGLE_ADMIN_SHARING_CONFIGURATION,
          title: "Sharing Configuration",
          active: canViewSharingConfiguration,
          parentId: 1,
        },
        {
          id: 26,
          path: ROUTES.GOOGLE_ADMIN_PROJECT_TYPE_RULES,
          title: "Manage Project Type Rules",
          active: canViewProjectTypeRules,
          parentId: 1,
        },
        {
          id: 27,
          path: ROUTES.GOOGLE_ADMIN_COST_CODE_RULES,
          title: "Manage Cost Code Rules",
          active: canViewCostCodeRules,
          parentId: 1,
        },
        {
          id: 28,
          path: ROUTES.GOOGLE_ADMIN_MISSING_PRODUCTS,
          title: "Missing Products",
          active: canViewProducts,
          parentId: 1,
        },
      ],
    },
  ];

  const normalizedAdminMenu = useMemo(
    () => normalizeAdminMenu(adminMenuItems),
    [adminMenuItems]
  );


  // return only active ones
  const filterVisibleMenuItems = (menuItems: any) => {
    let result: any = [];

    menuItems.forEach((item: any) => {
      if (item.active && item?.submenu) {
        result.push({
          ...item,
          submenu: item?.submenu.filter((i: any) => i.active),
        });
      } else if (item.active) {
        result.push(item);
      }
    });

    return result;
  };

  useEffect(() => {
    const matchedMenu: any[] = [];

    adminMenuItems.forEach((menu) => {
      if (!menu.active) return;

      menu.children.forEach((child) => {
        if (child.path && currentLocation.startsWith(child.path)) {
          matchedMenu.push(menu);
        } else if ("submenu" in child && Array.isArray(child.submenu)) {
          child.submenu.forEach((sub) => {
            if (currentLocation.startsWith(sub.path)) {
              matchedMenu.push(menu);
            }
          });
        }
      });
    });

    if (matchedMenu.length) {
      setVisibleAdminMenuItems(() =>
        filterVisibleMenuItems(matchedMenu[0].children)
      );
    }
  }, [currentLocation]);

  const closeAdminMenu = () => {
    setVisibleAdminMenuItems([]);
  };

  const openAdminMenu = (items: any) => {
    const filteredItems = filterVisibleMenuItems(items);

    setVisibleAdminMenuItems(filteredItems);
    if (filteredItems.length > 0) {
      navigate(
        filteredItems[0].submenu
          ? filteredItems[0].submenu[0].path
          : filteredItems[0].path
      );
    } else {
      console.info("No menu items to display.");
    }
  };

  useEffect(() => {
    const timezoneFromLocalStorage = localStorage.getItem("timezone");

    if (timezoneFromLocalStorage) {
      mercuryDispatch({
        type: ActionTypes.UPDATE_TIMEZONE,
        payload: timezoneFromLocalStorage,
      });
    } else {
      // guess the timezone from moment
      const timezoneFromMoment = momentTZ.tz.guess();

      mercuryDispatch({
        type: ActionTypes.UPDATE_TIMEZONE,
        payload: timezoneFromMoment,
      });
      localStorage.setItem(`timezone`, timezoneFromMoment);
    }

    // fix to update timezone on all opened tabs
    window.addEventListener(`storage`, (e) => {
      if (e.key === "timezone") {
        mercuryDispatch({
          type: ActionTypes.UPDATE_TIMEZONE,
          payload: e.newValue,
        });
      }
    });
  }, [mercuryDispatch]);


  if (loginRequired && currentLocation === ROUTES.UNAUTHENTICATED) {
    return (
      <UnauthorizedPage />
    );
  }

  if (loginRequired && currentLocation === ROUTES.AUTHERROR) {
    return (
      <Result
        title="Authentication Error"
        icon={<ExclamationCircleOutlined style={{ fontSize: '48px', color: '#e60054' }} />}
        subTitle="Sorry, there was an issue authenticating your account."
        extra={
          <Link to={ROUTES.MAIN}>Retry login</Link>
        }
      />

    );
  }

  if (loginRequired) {
    return <LoginPage />;
  }

  if (!hasAnyPermissions) {
    return <UnauthorizedPage />;
  }

  // https://ant.design/docs/react/customize-theme
  // Note: This doesn't apply to modals, notifications and other components
  // that are being created outside of DOM
  return (
    <div className="App">
      <ConfigProvider
        theme={{
          token: {
            // Seed Token
            colorPrimary: "#8353fd",
            colorLink: "#3e016f",
            colorInfo: "#3e016f",
            colorSuccess: "#3e016f",
            fontFamily: "'Epilogue', sans-serif",
            fontFamilyCode: "'Epilogue', sans-serif",
            colorTextBase: "#282c36",
            borderRadius: 4,
            // Map Token
            colorInfoActive: "#3e016f",
            colorPrimaryHover: "#3e016f",
            colorLinkActive: "#3e016f",
            colorPrimaryTextHover: "#3e016f",
            colorInfoTextHover: "#3e016f",
            // Alias Token
            colorBgContainer: "#fafafa",
          },
        }}
      >
        <Layout className="layout">
          <Header
            adminMenuData={{
              adminMenuItems: normalizedAdminMenu,
              openAdminMenu,
              closeAdminMenu,
              currentMenuItem,
              handleMainMenuSelection,
            }}
          />
          <Layout>
            {visibleAdminMenuItems.length > 0 && (
              <SideAdminMenu items={visibleAdminMenuItems}/>
            )}
            <ContentSection />
          </Layout>
        </Layout>
        <SessionExpiredModal />
      </ConfigProvider>
    </div>
  );
}

export default App;
